import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Users2, Check, MessageSquare, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function VerificationQueue() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // {obsId: true}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/verification/queue?limit=30');
      setItems(data.observations || []);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async (o) => {
    setPending(p => ({ ...p, [o.observation_id]: true }));
    try {
      await api.post(`/verification/${o.observation_id}/confirm`, { notes: null });
      setItems(prev => prev.filter(x => x.observation_id !== o.observation_id));
      toast.success('Confirmed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Confirm failed');
    } finally {
      setPending(p => { const n = { ...p }; delete n[o.observation_id]; return n; });
    }
  };

  return (
    <div className="min-h-screen bg-[#04120c] text-foreground pb-24" data-testid="verification-queue-root">
      <div className="max-w-2xl mx-auto px-4 py-4">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-xs text-white/60 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Users2 className="w-4 h-4 text-primary-light" />
          <h1 className="font-serif text-2xl">Community Verification</h1>
        </div>
        <p className="text-xs text-white/60 mb-4">
          Help other explorers by confirming high-confidence observations. Every verified record
          strengthens the WildQuest Biodiversity Network.
        </p>
        {loading && <p className="text-sm text-white/60 py-8 text-center">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="p-6 rounded-3xl glass border border-white/10 text-center text-white/70" data-testid="queue-empty">
            The queue is empty — check back soon.
          </div>
        )}
        <ul className="space-y-3">
          {items.map(o => (
            <motion.li
              key={o.observation_id}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-3xl glass border border-white/10"
              data-testid={`queue-row-${o.observation_id}`}
            >
              <div className="flex items-start gap-3">
                {o.image_path && (
                  <img
                    src={`${api.defaults.baseURL}/files/${o.image_path}`}
                    alt=""
                    className="w-20 h-20 rounded-2xl object-cover border border-white/10"
                    onClick={() => nav(`/observations/${o.observation_id}`)}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{o.species_common_name}</p>
                  <p className="italic text-xs text-white/60 truncate">{o.species_scientific_name}</p>
                  {o.ai_confidence != null && (
                    <p className="text-[10px] uppercase tracking-widest text-primary-light mt-1">
                      AI confidence: {Math.round(o.ai_confidence * 100)}%
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      data-testid={`btn-confirm-${o.observation_id}`}
                      disabled={!!pending[o.observation_id]}
                      onClick={() => confirm(o)}
                      className="px-3 py-1.5 rounded-full bg-primary text-white text-xs flex items-center gap-1 disabled:opacity-50"
                    >
                      <Check className="w-3 h-3" /> Confirm
                    </button>
                    <button
                      onClick={() => nav(`/observations/${o.observation_id}`)}
                      className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs flex items-center gap-1"
                    >
                      <MessageSquare className="w-3 h-3" /> Discuss
                    </button>
                  </div>
                </div>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}
